import { Card, CardHeader, CardContent, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, IconButton, Avatar, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TableChartIcon from '@mui/icons-material/TableChart';
import { useTranslation } from '../../../hooks/useTranslation';

export const WidgetTable = ({ onRemove, isEditMode }) => {
    const t = useTranslation('Dashboard.json');
    const handleRemove = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onRemove();
    };

    const orders = [
        { id: '#001', client: 'Jean Dupont', amount: '150€', statusKey: 'paid' },
        { id: '#002', client: 'Marie Martin', amount: '250€', statusKey: 'in_progress' },
        { id: '#003', client: 'Pierre Durand', amount: '320€', statusKey: 'paid' },
        { id: '#004', client: 'Sophie Bernard', amount: '180€', statusKey: 'pending' }
    ];

    const getStatusColor = (statusKey) => {
        switch(statusKey) {
            case 'paid': return 'success';
            case 'in_progress': return 'info';
            case 'pending': return 'warning';
            default: return 'default';
        }
    };

    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                bgcolor: 'background.paper',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
                    borderColor: 'primary.light'
                }
            }}
        >
            <CardHeader
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#4CAF5020', width: 32, height: 32 }}>
                            <TableChartIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                        </Avatar>
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '15px', fontFamily: 'Poppins' }}>
                                {t('widget.table.title')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {t('widget.table.subtitle', [t('widget.table.subtitle.today')])}
                            </Typography>
                        </Box>
                    </Box>
                }
                action={
                    isEditMode && (
                        <IconButton
                            className="widget-remove-btn"
                            onClick={handleRemove}
                            onMouseDown={(e) => e.stopPropagation()}
                            size="small"
                            sx={{
                                opacity: 0,
                                pointerEvents: 'none',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: 'error.light',
                                    color: 'error.main'
                                }
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    )
                }
                sx={{
                    cursor: 'move',
                    pb: 1,
                    bgcolor: 'transparent',
                    '&:hover .widget-remove-btn': {
                        opacity: 1,
                        pointerEvents: 'all'
                    }
                }}
                className="widget-header"
            />
            <CardContent sx={{ flex: 1, pt: 0, overflow: 'auto' }}>
                <TableContainer>
                    <Table size="small" sx={{ minWidth: 280 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, fontFamily: 'Poppins', fontSize: 'clamp(10px, 1vw, 12px)' }}>{t('widget.table.column.id')}</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontFamily: 'Poppins', fontSize: 'clamp(10px, 1vw, 12px)' }}>{t('widget.table.column.client')}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, fontFamily: 'Poppins', fontSize: 'clamp(10px, 1vw, 12px)' }}>{t('widget.table.column.amount')}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, fontFamily: 'Poppins', fontSize: 'clamp(10px, 1vw, 12px)' }}>{t('widget.table.column.status')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.map((order) => (
                                <TableRow
                                    key={order.id}
                                    sx={{
                                        '&:hover': { bgcolor: 'action.hover' },
                                        transition: 'background-color 0.2s'
                                    }}
                                >
                                    <TableCell sx={{ fontFamily: 'Poppins', fontSize: 'clamp(11px, 1.2vw, 13px)', fontWeight: 500 }}>
                                        {order.id}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'Poppins', fontSize: 'clamp(11px, 1.2vw, 13px)' }}>
                                        {order.client}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontFamily: 'Poppins', fontSize: 'clamp(11px, 1.2vw, 13px)', fontWeight: 600 }}>
                                        {order.amount}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            label={t(`widget.table.status.${order.statusKey}`)}
                                            color={getStatusColor(order.statusKey)}
                                            size="small"
                                            sx={{
                                                fontSize: 'clamp(9px, 1vw, 10px)',
                                                fontFamily: 'Poppins',
                                                height: 20
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};
